import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, Drawer, FormField, toast } from "@outpave/ds";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import { isEqual } from "lodash";
import {
  GET_AUTO_BALANCE_RULES,
  SET_AUTO_BALANCE_RULE,
  REMOVE_AUTO_BALANCE_RULE,
  ENABLE_AUTO_BALANCE_RULE,
} from "../../../graphql/company";
import maintainBalance from "../../../validations/maintainBalance";
import CurrencyInput from "../common/CurrencyInput";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import { BankAccount } from "hooks/useGetBankAccounts";
import { banksOnConnection, keepOrPick } from "utils/accounts/moneyMovement";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  AccountSelect,
  ConfirmTransfer,
  MemoField,
  TransferAccountsRow,
} from "./TransferFormFields";
import AutoBalanceAuthorizationDialog from "./AutoBalanceAuthorizationDialog";

interface FormikMaintainBalanceProps {
  minimumBalance: number;
  targetBalance: number;
  memo: string;
  confirmTransfer: boolean;
}

interface AutoBalanceRule {
  fromAccountId?: string;
  toAccountId: string;
  minimumBalance: number;
  targetBalance: number;
  memo?: string;
  isDisabled?: boolean;
}

const ruleValues = (rule: AutoBalanceRule): FormikMaintainBalanceProps => ({
  minimumBalance: rule.minimumBalance,
  targetBalance: rule.targetBalance,
  memo: rule.memo || "",
  confirmTransfer: false,
});

interface AutoBalanceDrawerProps {
  open: boolean;
  /** The account the menu was opened from, preferred over the first one. */
  selectedToAccountId?: string;
  fromAccountsData: BankAccount[];
  toAccountsData: BankAccount[];
  handleCloseDrawer: (isReloadData: boolean) => void;
  refetchAccounts: () => void;
}

const AutoBalanceDrawer: React.FC<AutoBalanceDrawerProps> = (props) => {
  const {
    open,
    selectedToAccountId,
    fromAccountsData,
    toAccountsData,
    handleCloseDrawer,
    refetchAccounts,
  } = props;

  const { t } = useTranslation();

  const {
    data: rulesData,
    loading: loadingBalance,
    refetch: refetchBalanceSettings,
  } = useQuery(GET_AUTO_BALANCE_RULES, {
    fetchPolicy: "network-only",
  });

  const [maintainBalanceFunct, { loading: loadingMaintainBalance }] =
    useMutation(SET_AUTO_BALANCE_RULE, {
      onCompleted: () => {
        formik.resetForm();
        setEditingBalanceSettings(false);
        handleCloseDrawer(true);
        toast.success(t("Balance settings successfully updated"));
        refetchBalanceSettings();
      },
      onError: (err) => {
        toast.error(t(err.message));
      },
    });

  const [
    removeCompanyBalanceSetting,
    { loading: loadingRemoveCompanyBalanceSetting },
  ] = useMutation(REMOVE_AUTO_BALANCE_RULE, {
    onCompleted: () => {
      formik.resetForm();
      setEditingBalanceSettings(false);
      setConfirmRemove(false);
      handleCloseDrawer(true);
      toast.success(t("Auto Balance successfully removed"));
      refetchBalanceSettings();
    },
    onError: (err) => {
      toast.error(t(err.message));
    },
  });

  const [bankLinkActive, setBankLinkActive] = useState(false);

  const [
    enableCompanyBalanceSetting,
    { loading: loadingEnableCompanyBalanceSetting },
  ] = useMutation(ENABLE_AUTO_BALANCE_RULE, {
    onCompleted: () => {
      toast.success(t("autoTransferRulesEnabled"));
      refetchBalanceSettings();
    },
    onError: (err) => {
      toast.error(t(err.message));
    },
  });

  const [fromAccount, setFromAccount] = useState("");
  const [toAccount, setToAccount] = useState("");
  // Each program keeps its own rule, so the drawer shows the one on the program chosen under To.
  const programRule: AutoBalanceRule | undefined = (
    rulesData?.getAutoBalanceRules as AutoBalanceRule[] | undefined
  )?.find((rule) => rule.toAccountId === toAccount);

  const hasBalanceSettings = !!programRule;
  const isBalanceSettingDisabled =
    hasBalanceSettings && Boolean(programRule?.isDisabled);

  const [editingBalanceSettings, setEditingBalanceSettings] =
    useState<boolean>(false);
  const [showAuthorization, setShowAuthorization] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (open) {
      setEditingBalanceSettings(!hasBalanceSettings);
    }
  }, [open, hasBalanceSettings]);

  const handleEditBalance = () => {
    formik.setFieldValue("confirmTransfer", false);
    setEditingBalanceSettings(!editingBalanceSettings);
  };

  const formik = useFormik<FormikMaintainBalanceProps>({
    initialValues: {
      minimumBalance: 2000.0,
      targetBalance: 5000.0,
      memo: "",
      confirmTransfer: false,
    },
    validationSchema: maintainBalance,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { minimumBalance, targetBalance, memo } = values;
      if (!fromAccount) {
        toast.error(t("Please select a funding account"));
        return;
      }
      const input = {
        minimumBalance,
        targetBalance,
        memo,
        fromAccountId: fromAccount,
        toAccountId: toAccount,
      };
      await maintainBalanceFunct({
        variables: {
          input,
        },
      });
    },
  });

  const { handleFormikHelperText, handleFormikError } =
    useFormikHelpers(formik);

  const savedValues = programRule
    ? ruleValues(programRule)
    : formik.initialValues;

  useEffect(() => {
    formik.setValues(savedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programRule, open, editingBalanceSettings]);

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstToAccountId = toAccountsData?.[0]?.id;
  useEffect(() => {
    if (!open || !toAccountsData?.length) return;
    const preferred = toAccountsData.find(
      (account: BankAccount) => account.id === selectedToAccountId,
    );
    setToAccount((preferred ?? toAccountsData[0]).id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedToAccountId, firstToAccountId]);

  // A program's rule names its bank, so choosing the program shows that bank.
  const ruleFromAccountId = programRule?.fromAccountId;
  useEffect(() => {
    if (open && ruleFromAccountId) setFromAccount(ruleFromAccountId);
  }, [open, toAccount, ruleFromAccountId]);

  const toProgram = toAccountsData.find(({ id }) => id === toAccount);
  const fromBanks = useMemo(
    () => banksOnConnection(fromAccountsData, toProgram),
    [fromAccountsData, toProgram],
  );

  useEffect(() => {
    if (!open) return;
    setFromAccount((current) => keepOrPick(fromBanks, current));
  }, [open, fromBanks]);

  // The save gates dismissal; the balance fetch only veils, so a hanging
  // query cannot trap the user. The legacy header X skipped the
  // setEditingBalanceSettings reset the backdrop did, one path now.
  const busy = loadingMaintainBalance;

  const leave = () => {
    formik.resetForm();
    setEditingBalanceSettings(false);
    setShowAuthorization(false);
    setConfirmRemove(false);
    handleCloseDrawer(false);
  };
  const { requestLeave, dialog } = useUnsavedChangesGuard(
    editingBalanceSettings && !isEqual(formik.values, savedValues),
    leave,
  );

  const handleClose = () => {
    if (busy) return;
    requestLeave();
  };

  return (
    <>
      <Drawer
        open={open}
        disableEnforceFocus={bankLinkActive}
        onClose={handleClose}
        title={t("Auto Balance")}
        width="544px"
        loading={loadingBalance || busy}
        footer={
          editingBalanceSettings ? (
            <Button
              variant="primary-filled"
              // No `isValid` gate: `handleSubmit` validates, touches every
              // field so the errors paint, and refuses to submit.
              disabled={
                !formik.values.confirmTransfer || loadingMaintainBalance
              }
              onClick={() => formik.handleSubmit()}
            >
              {loadingMaintainBalance
                ? t("Saving")
                : hasBalanceSettings
                  ? t("Maintain")
                  : t("Set up Auto Balance")}
            </Button>
          ) : undefined
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <Stack gap="var(--space-6)">
            <TransferAccountsRow
              from={
                <AccountSelect
                  name="fromAccount"
                  ariaLabel={t("From")}
                  value={fromAccount}
                  accounts={fromBanks}
                  onSelect={setFromAccount}
                  onAccountAdded={setFromAccount}
                  onBankLinkActiveChange={setBankLinkActive}
                  refetchAccounts={refetchAccounts}
                  program={toProgram?.cardProgram}
                />
              }
              to={
                <AccountSelect
                  name="toAccount"
                  ariaLabel={t("To")}
                  value={toAccount}
                  accounts={toAccountsData}
                  onSelect={setToAccount}
                />
              }
            />

            <Inline justify="between" wrap={false} sx={{ width: "100%" }}>
              <Typography variant="subtitle1" component="h3">
                {!hasBalanceSettings
                  ? t("Set up Auto Balance")
                  : isBalanceSettingDisabled
                    ? t("Auto Balance is off")
                    : t("Auto Balance is enabled")}
              </Typography>

              {hasBalanceSettings &&
                (editingBalanceSettings ? (
                  <Button
                    size="small"
                    variant="default-outlined"
                    onClick={handleEditBalance}
                  >
                    {t("Cancel")}
                  </Button>
                ) : (
                  <Inline gap="var(--space-2)" wrap={false}>
                    <Button
                      size="small"
                      variant="default-outlined"
                      onClick={handleEditBalance}
                    >
                      {t("Edit")}
                    </Button>
                    <Button
                      size="small"
                      variant="danger-outlined"
                      onClick={() => setConfirmRemove(true)}
                    >
                      {t("Remove")}
                    </Button>
                  </Inline>
                ))}
            </Inline>

            {isBalanceSettingDisabled && !editingBalanceSettings && (
              <Stack gap="var(--space-3)" sx={{ alignItems: "flex-start" }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("AutoBalanceOffDescription")}
                </Typography>
                <Button
                  size="small"
                  variant="primary-filled"
                  disabled={loadingEnableCompanyBalanceSetting}
                  onClick={() =>
                    enableCompanyBalanceSetting({
                      variables: { programId: toAccount },
                    })
                  }
                >
                  {t("reEnableAutoTransfer")}
                </Button>
              </Stack>
            )}

            <Stack gap="var(--space-3)">
              <FormField
                label={t("Target Balance")}
                htmlFor="targetBalance"
                required
                disabled={!editingBalanceSettings}
                sx={{ maxWidth: 320 }}
              >
                <CurrencyInput
                  id="targetBalance"
                  name="targetBalance"
                  value={formik.values.targetBalance}
                  fullWidth
                  size="md"
                  required
                  onChange={(raw) => formik.setFieldValue("targetBalance", raw)}
                  onBlur={formik.handleBlur}
                  error={handleFormikError("targetBalance")}
                  helperText={handleFormikHelperText("targetBalance")}
                  disabled={!editingBalanceSettings}
                />
              </FormField>
            </Stack>

            <FormField
              label={t("Minimum Balance")}
              htmlFor="minimumBalance"
              required
              disabled={!editingBalanceSettings}
              sx={{ maxWidth: 320 }}
            >
              <CurrencyInput
                id="minimumBalance"
                name="minimumBalance"
                value={formik.values.minimumBalance}
                fullWidth
                size="md"
                required
                onChange={(raw) => formik.setFieldValue("minimumBalance", raw)}
                onBlur={formik.handleBlur}
                error={handleFormikError("minimumBalance")}
                helperText={handleFormikHelperText("minimumBalance")}
                disabled={!editingBalanceSettings}
              />
            </FormField>

            <Typography variant="body2">
              {t("MaintainBalanceDescription")}
            </Typography>

            <MemoField
              value={formik.values.memo}
              onChange={(memo) => formik.setFieldValue("memo", memo)}
              label={t("Auto Balance Memo")}
              placeholder={t("Add a note for this transfer") as string}
              disabled={!editingBalanceSettings}
            />

            <ConfirmTransfer
              checked={formik.values.confirmTransfer}
              // Ticking asks for the agreement rather than setting the field:
              // the box IS the record that the authorization was accepted, so
              // only accepting it may tick it.
              onChange={(checked) => {
                if (checked) {
                  setShowAuthorization(true);
                  return;
                }
                formik.setFieldValue("confirmTransfer", false);
              }}
              disabled={!editingBalanceSettings}
              label={t("AchDebitAuthorization")}
            />
          </Stack>
        </form>

        <ConfirmDialog
          open={confirmRemove}
          title={t("Remove Auto Balance")}
          message={t("RemoveAutoBalanceConfirm")}
          confirmLabel={t("Remove")}
          cancelLabel={t("Cancel")}
          busy={loadingRemoveCompanyBalanceSetting}
          onConfirm={() =>
            removeCompanyBalanceSetting({ variables: { programId: toAccount } })
          }
          onCancel={() => setConfirmRemove(false)}
        />

        <AutoBalanceAuthorizationDialog
          open={showAuthorization}
          onAccept={() => {
            setShowAuthorization(false);
            formik.setFieldValue("confirmTransfer", true);
          }}
          onClose={() => setShowAuthorization(false)}
        />
      </Drawer>
      {dialog}
    </>
  );
};

export default AutoBalanceDrawer;
